import React, { useRef } from "react";
import { FilterState } from "../types/orderTypes";
import CommonButtons from "src/shared/components/CommonButtons";
import { useUserTheme } from "src/shared/utils/useUserTheme";

interface OrderFiltersProps {
  filterState: FilterState;
  onFilterChange: (updates: Partial<FilterState>) => void;
  onImport: (file: File) => void;
  onExport: (format: "CSV" | "PDF") => void;
  loading?: boolean;
  showCustomDateApply?: boolean;
  onApplyCustomDate?: () => void;
}

const OrderFilters: React.FC<OrderFiltersProps> = ({
  filterState,
  onFilterChange,
  onImport,
  onExport,
  loading = false,
  showCustomDateApply = false,
  onApplyCustomDate,
}) => {
  const { fontClass, borderHighlight } = useUserTheme();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onImport(file);
      inputRef.current!.value = "";
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({ searchQuery: e.target.value });
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFilterChange({ filter: e.target.value });
  };

  const handleExportChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const format = e.target.value as "CSV" | "PDF";
    if (format) onExport(format);
    e.target.value = "";
  };

  const handleDateChange = (field: "selectedDate" | "selectedDate2", value: string) => {
    onFilterChange({ [field]: value });
  };

  const isCustomDateValid = filterState.selectedDate && filterState.selectedDate2;

  return (
    <div className='flex flex-col gap-4 p-3 bg-white border-b border-gray-200'>
      <div className='flex flex-col lg:flex-row w-full gap-4'>
        <div className='flex flex-col sm:flex-row flex-wrap gap-4 sm:items-center w-full lg:w-auto'>
          <select
            value={filterState.filter}
            onChange={handleFilterChange}
            className={`px-4 py-2 border focus:outline-none rounded-lg bg-white text-gray-700 font-medium min-w-[170px] ${borderHighlight} ${fontClass} focus:ring-2`}
            disabled={loading}
          >
            <option value='' disabled>
              Select Date Range
            </option>
            <option value='Today'>Today</option>
            <option value='Yesterday'>Yesterday</option>
            <option value='LastWeek'>Last Week</option>
            <option value='LastMonth'>Last Month</option>
            <option value='CustomizeDate'>Custom Date</option>
          </select>

          <div className='relative'>
            <CommonButtons label='Import' disabled={loading} onClick={() => inputRef.current?.click()} />
            <input
              ref={inputRef}
              type='file'
              className='absolute inset-0 opacity-0 cursor-pointer'
              onChange={handleFileChange}
              accept='.csv,.xlsx,.xls'
              disabled={loading}
            />
          </div>

          <div className='flex items-center gap-2 bg-gray-50 rounded-lg border border-gray-200 px-4 py-2'>
            <label className='text-sm font-medium text-gray-700'>Export:</label>
            <select
              onChange={handleExportChange}
              disabled={loading}
              className={`bg-white border-none focus:outline-none ${borderHighlight} ${fontClass} text-sm font-medium text-gray-700 focus:ring-0 cursor-pointer`}
              defaultValue=''
            >
              <option value='' disabled>
                Format
              </option>
              <option value='CSV'>CSV</option>
              <option value='PDF'>PDF</option>
            </select>
          </div>
        </div>

        <div className='flex-1 flex justify-end w-full'>
          <div className='w-full sm:w-64 '>
            <input
              placeholder='Search by order ID, customer, etc...'
              value={filterState.searchQuery}
              onChange={handleSearchChange}
              className={`w-full px-5 py-2.5 border rounded-3xl focus:outline-none focus:ring-2 ${borderHighlight} ${fontClass} text-sm text-black`}
            />
          </div>
        </div>
      </div>

      {filterState.filter === "CustomizeDate" && (
        <div className='flex flex-col sm:flex-row flex-wrap gap-4 bg-blue-50 p-4 rounded-lg border border-blue-200'>
          <div className='flex items-center gap-2'>
            <label className='text-sm text-gray-700'>From:</label>
            <input
              type='date'
              value={filterState.selectedDate}
              onChange={(e) => handleDateChange("selectedDate", e.target.value)}
              className='px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500'
              disabled={loading}
            />
          </div>

          <div className='flex items-center gap-2'>
            <label className='text-sm text-gray-700'>To:</label>
            <input
              type='date'
              value={filterState.selectedDate2}
              onChange={(e) => handleDateChange("selectedDate2", e.target.value)}
              className='px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500'
              disabled={loading}
            />
          </div>

          {showCustomDateApply ? (
            <button
              onClick={onApplyCustomDate}
              disabled={!isCustomDateValid || loading}
              className='px-4 py-2 bg-blue-600 text-white rounded-md text-sm disabled:opacity-50'
            >
              Apply
            </button>
          ) : (
            <div className='text-sm text-gray-600'>{isCustomDateValid ? "Dates applied automatically" : "Select both dates to load data"}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderFilters;
